import { Accordion, AccordionDetails, AccordionSummary, Box, Grid2, Typography } from '@mui/material'
import { initiateNewThread, selectFloatingChatActiveThreadId, selectIfAssistanceResponseLoading, selectNewThreadId, selectThreadFromHistoryById, selectThreadHistoryLoadingError, sendQueryToThread, updateFloatingChatId } from '../../store/slices/threadsSlice';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react'

import AIRepsonseLoadingProgress from './AIRepsonseLoadingProgress';
import { AppDispatch } from '../../store/store';
import AssistantResponse from '../threads/AssistantResponse';
import BaloonShaped from '../styled/BaloonShaped';
import { EXAMPLE_PROMP_SUGGESTIONS } from '../../constants/SuggestedPrompts';
import ErrorState from './ErrorState';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { MESSAGES } from '../../constants/Messages';
import Markdown from 'react-markdown';
import PromptInput from './PromptInput'
import { SCROLL_BAR_STYLE_PROPS } from '../../constants/ScrollbarProps';
import UserQuery from '../threads/UserQuery';
import { selectAppliedDataSourcesAsRequest } from '../../store/slices/dataSourcesSlice';

const NEW_THREAD_ID = "new";

export default function FloatingChat() {
    const dispatch = useDispatch<AppDispatch>();

    const focusElementRef = useRef<HTMLDivElement>(null);

    const threadId = useSelector(selectFloatingChatActiveThreadId);
    const [activeChatId, setActiveChatId] = useState(threadId)
    const [isChatActive, setIsChatActive] = useState(false);
    const thread = useSelector(selectThreadFromHistoryById(activeChatId));
    const selectedDataSources = useSelector(selectAppliedDataSourcesAsRequest, shallowEqual);
    const newThreadId = useSelector(selectNewThreadId)

    const loadingResponse = useSelector(selectIfAssistanceResponseLoading);
    const errorLoadingReponse = useSelector(selectThreadHistoryLoadingError);

    const [isTextStreaming, setIsTextStreaming] = useState(false);

    const onPromptSubmit = async (query: string) => {
        setIsChatActive(true);
        const payload = {
            threadId: activeChatId,
            request: {
                content: {
                    text: query,
                    sources: selectedDataSources
                },
                role: "user",
            }
        };
        if (activeChatId === NEW_THREAD_ID) {
            dispatch(initiateNewThread(payload))
        } else {
            dispatch(sendQueryToThread(payload))
        }
        autoScroll();
    }

    const handleAccordionChange = () => {
        if (activeChatId === NEW_THREAD_ID)
            setIsChatActive(false)
        else setIsChatActive(prev => !prev)
    }

    const getChatIcon = () => {
        if (thread && thread.entries.length > 0)
            return <ExpandLessIcon />
        else <></>
    }

    const handleOnTextStreaming = (value: boolean) => {
        setIsTextStreaming(value);
        autoScroll();
    }

    const autoScroll = () => {
        if (focusElementRef.current && isTextStreaming) {
            focusElementRef.current?.scrollIntoView()
        }
    }

    useEffect(() => {
        if (focusElementRef.current) {
            focusElementRef.current?.scrollIntoView({ behavior: 'smooth' })
        }
    }, [thread])

    useEffect(() => {
        if (activeChatId === NEW_THREAD_ID) {
            setActiveChatId(newThreadId)
            dispatch(updateFloatingChatId({
                thread_id: newThreadId
            }));
        }
    }, [activeChatId, dispatch, newThreadId])

    return (
        <Grid2 size={{ md: 3.5, xl: 3.25 }} sx={{
            position: 'fixed',
            zIndex: 1000,
            bottom: '3rem',
            right: '5rem',
            backgroundColor: 'rgba(255, 255, 255, 97.5%)',
            minHeight: '150px',
            display: 'grid',
            gridTemplateColumns: '1fr',
            justifyItems: 'flex-end',
            rowGap: 1,
            borderTopLeftRadius: 20,
            borderBottomRightRadius: 20,
            border: '1px solid #00A9B1',
            boxShadow: '0px 7px 18px 0px #083C4999, 0px 0px 14px 0px #80E1E9',
            padding: 2,
        }}>
            <Accordion component={Box}
                expanded={isChatActive === true}
                onChange={handleAccordionChange}
                elevation={0}
                sx={{
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'transparent',
                }}>
                <AccordionSummary
                    expandIcon={getChatIcon()}
                    sx={{
                        padding: 0,
                        margin: 0,
                        border: 0,
                        borderBottom: 1,
                        borderStyle: 'solid',
                        borderImage: 'linear-gradient(to left, #69879B, transparent) 1'
                    }}>
                    <Typography variant='h5'
                        sx={{
                            width: '100%',
                            height: 'max-content',
                            fontWeight: 700,
                            margin: 0,
                            background: 'linear-gradient(93.62deg, #5A4EB4 -79%, #00A9B1 38.35%, #2E79B3 155.71%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                        ASK iHUB
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{
                    width: '100%',
                    minHeight: '200px',
                    maxHeight: '500px',
                    overflowX: 'hidden',
                    overflowY: 'scroll',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: 2,
                    ...SCROLL_BAR_STYLE_PROPS,
                }}>
                    {thread && thread.entries.length > 0 && thread.entries.map((entry: any, index: number) => {
                        const isRequestCategory = entry.category === 'request';
                        return (
                            <Box key={entry.id ?? index}
                                sx={{
                                    maxWidth: '90%',
                                    position: 'relative',
                                    boxSizing: 'border-box',
                                    alignSelf: isRequestCategory ? 'flex-end' : '',
                                    borderRadius: 2,
                                }}>
                                {entry.category === 'request' && <BaloonShaped type={entry.category}>
                                    <UserQuery
                                        isFloatingChat={true}
                                        query={entry.data.content.text!}
                                        created_at={entry.created_at}
                                        sources={entry.data.content.sources ?? {}}></UserQuery>
                                </BaloonShaped>}
                                {entry.category === 'response' && <BaloonShaped type={entry.category}>
                                    <AssistantResponse
                                        isStreaming={entry.isStreaming}
                                        onTextStreamingFn={handleOnTextStreaming}
                                        isFloatingChat={true}
                                        thread_id={entry.thread_id}
                                        entry_id={entry.entry_id}
                                        sources={entry.data.content.sources}
                                        text={entry.data.content.text!}
                                        time={entry.created_at}
                                        plot={entry.data.content.visual}></AssistantResponse>
                                </BaloonShaped>
                                }
                            </Box>
                        )
                    })}
                    {!loadingResponse && errorLoadingReponse !== '' && <ErrorState
                        errorMessage={errorLoadingReponse as string} />}
                    {loadingResponse && <AIRepsonseLoadingProgress></AIRepsonseLoadingProgress>}
                    <Box ref={focusElementRef}></Box>
                </AccordionDetails>
            </Accordion>
            {(!thread || thread.entries.length === 0) && <Box sx={{
                padding: 2,
            }}>
                <Typography component={'div'} sx={{
                    textAlign: 'center',
                    lineHeight: 1,
                    '&>p': {
                        margin: 0.5,
                        '&>strong': {
                            color: '#009CA4'
                        },
                    }
                }}>
                    <Markdown>{MESSAGES.ASK_GUARDIAN_PRE_TEXT}</Markdown>
                </Typography>
            </Box>
            }
            <PromptInput disable={loadingResponse || isTextStreaming}
                onPromptSubmit={onPromptSubmit}
                suggestions={EXAMPLE_PROMP_SUGGESTIONS}></PromptInput>
        </Grid2>
    )
}